/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { useCallback, useEffect, useState } from 'react';

/**
 * Light, dark, or whatever the machine is set to.
 *
 * **Three states, not two**, because "nobody has said" and "somebody chose
 * light" are different facts and only one of them should follow the machine when
 * it flips at sunset. The **preference** is one of three and lives in
 * localStorage; the **resolved** answer is one of two and lives in `data-theme`
 * on `<html>`. CSS only ever sees the resolved one, which is why `index.css` has
 * exactly one dark block and no `prefers-color-scheme` duplicate of it.
 *
 * **The default is `light`, not `system`.** It was `system`, on the reasoning
 * that following the machine is the least presumptuous thing an app can do.
 * Abhishek asked for light: this is an internal tool people open next to other
 * internal tools, most of which are light whatever the laptop is set to, and a
 * board that comes up dark because of an OS setting somebody forgot they changed
 * reads as the app being broken rather than as the app being considerate. Dark
 * and System are both one click away and *stay* chosen.
 *
 * That inverted how "system" is stored. It used to be the **absence** of the
 * key — absence meant nobody had said, which meant follow the machine. Absence
 * now means light, so choosing System has to be written down like any other
 * choice. Anyone who had never touched the switcher moves from "follows the
 * machine" to "light", which is exactly the change that was asked for.
 */
export type ThemePref = 'system' | 'light' | 'dark';
export type Resolved = 'light' | 'dark';

/**
 * **`qollabTheme`, not `kanbanTheme`.**
 *
 * The other two keys in this app — `kanbanBoardSort` and `todoLayout` — are
 * vanilla's, kept letter for letter because renaming a storage key silently
 * discards what every person had saved under it. That reasoning does not reach a
 * key nobody has ever written to. This one is new, so it gets the product's
 * actual name.
 */
export const THEME_KEY = 'qollabTheme';

const MEDIA = '(prefers-color-scheme: dark)';

/**
 * **Three stored values now, and an unreadable store means light.**
 *
 * `'system'` reads back as itself rather than being inferred from a missing key
 * — see the note at the top. Anything else, including `null` and the value a
 * future version might write, falls to `light`: an unrecognised preference is
 * the same situation as no preference, and both should land somewhere
 * predictable rather than on whatever the laptop happens to be set to.
 */
export function readPref(): ThemePref {
  // Private browsing and locked-down profiles can make this throw rather than
  // return null. A theme is not worth a blank page.
  try {
    const raw = localStorage.getItem(THEME_KEY);
    return raw === 'light' || raw === 'dark' || raw === 'system' ? raw : 'light';
  } catch {
    return 'light';
  }
}

export function resolve(pref: ThemePref): Resolved {
  if (pref !== 'system') return pref;
  return window.matchMedia(MEDIA).matches ? 'dark' : 'light';
}

export function apply(next: Resolved): void {
  document.documentElement.dataset.theme = next;
}

/**
 * The switcher's state.
 *
 * The three things it has to keep in step, all of which are easy to forget:
 *
 * - **the system changing under a "system" preference** — macOS and Windows both
 *   flip at dusk, and an app that only reads the query at startup sits there in
 *   the wrong theme until it is reloaded;
 * - **another tab** — Qollab is the kind of thing people keep open in three of
 *   them, and choosing dark in one while the others stay light looks like a bug
 *   in the app rather than a per-tab setting;
 * - **the attribute itself**, which was already set before React booted by the
 *   inline script in `index.html`. This hook must not undo that on mount, which
 *   is why the initial state is *read* rather than assumed.
 */
export function useTheme(): { pref: ThemePref; resolved: Resolved; setPref: (p: ThemePref) => void } {
  const [pref, setPrefState] = useState<ThemePref>(readPref);
  const [resolved, setResolved] = useState<Resolved>(() => resolve(readPref()));

  const setPref = useCallback((next: ThemePref) => {
    setPrefState(next);
    try {
      // **All three are written, including "system".** It used to be stored as
      // the absence of a choice, which was right while absence *meant* system;
      // now that absence means light, removing the key would quietly turn
      // "follow my machine" into "light" on the next load — the setting would
      // appear to take and then undo itself, which is the worst kind of bug to
      // report.
      localStorage.setItem(THEME_KEY, next);
    } catch {
      // Ignored on purpose: the theme still applies for this session below.
    }
    const now = resolve(next);
    setResolved(now);
    apply(now);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia(MEDIA);
    const onSystem = () => {
      // Only while nobody has chosen. Reapplying under an explicit preference
      // would let the operating system override the person.
      if (readPref() !== 'system') return;
      const now = resolve('system');
      setResolved(now);
      apply(now);
    };
    // `storage` fires in the *other* tabs, never the one that wrote — which is
    // exactly the behaviour wanted here.
    const onStorage = (e: StorageEvent) => {
      if (e.key !== null && e.key !== THEME_KEY) return;
      const next = readPref();
      setPrefState(next);
      const now = resolve(next);
      setResolved(now);
      apply(now);
    };
    mq.addEventListener('change', onSystem);
    window.addEventListener('storage', onStorage);
    return () => {
      mq.removeEventListener('change', onSystem);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  return { pref, resolved, setPref };
}
